/**
 * 版本号：v1.1.0
 * 变更记录：
 * - v1.0.0: 初始版本，基础建店成本组件
 * - v1.1.0: 添加装修成本上下限约束显示和提示功能
 */

import React, { useEffect, useState } from 'react';
import { calculateDecorationCost, DecorationCostRule, OperatingCostLimits } from '../utils/dataModel';

interface InitialCostsProps {
  initialCosts: {
    franchiseFee: number;
    brandUsageFee: number;
    designFee: number;
    storeServiceFee: number;
    equipmentCost: number;
    decorationCost: number;
    total: number;
  };
  setInitialCosts: React.Dispatch<React.SetStateAction<{
    franchiseFee: number;
    brandUsageFee: number;
    designFee: number;
    storeServiceFee: number;
    equipmentCost: number;
    decorationCost: number;
    total: number;
  }>>;
  basicInfo: {
    area: number;
  };
  filteredData: {
    initialCostsData?: {
      franchiseFee: number;
      brandUsageFee: number;
      designFee: number;
      storeServiceFee: number;
      equipmentCost: number;
      decorationCostRules: DecorationCostRule[];
      decorationCostMaxLimit?: number;
      decorationCostMinLimit?: number;
    };
    operatingCostLimits?: OperatingCostLimits;
  };
}

const InitialCosts: React.FC<InitialCostsProps> = ({ 
  initialCosts, 
  setInitialCosts, 
  basicInfo,
  filteredData
}) => {
  // 添加折叠状态，默认为展开
  const [isCollapsed, setIsCollapsed] = useState(true);

  // 添加装修成本限制状态
  const [decorationCostLimitInfo, setDecorationCostLimitInfo] = useState<{
    isLimited: boolean;
    limitType?: 'max' | 'min';
    originalCost?: number;
    limitValue?: number;
  }>({ isLimited: false });

  // 当Excel数据加载完成后，设置初始值
  useEffect(() => {
    if (!filteredData.initialCostsData) return;

    const { franchiseFee, brandUsageFee, designFee, storeServiceFee, equipmentCost, decorationCostRules, decorationCostMaxLimit, decorationCostMinLimit } = filteredData.initialCostsData;

    let decorationCost = 0;

    if (basicInfo.area > 0 && decorationCostRules.length > 0) {
      const originalCost = calculateDecorationCost(basicInfo.area, decorationCostRules);
      decorationCost = calculateDecorationCost(basicInfo.area, decorationCostRules, decorationCostMaxLimit, decorationCostMinLimit);

      let limitInfo: { isLimited: boolean; limitType?: 'max' | 'min'; originalCost?: number; limitValue?: number } = { isLimited: false };

      if (decorationCostMaxLimit !== undefined && originalCost > decorationCostMaxLimit) {
        limitInfo = { isLimited: true, limitType: 'max', originalCost, limitValue: decorationCostMaxLimit };
      } else if (decorationCostMinLimit !== undefined && originalCost < decorationCostMinLimit) {
        limitInfo = { isLimited: true, limitType: 'min', originalCost, limitValue: decorationCostMinLimit };
      }

      setDecorationCostLimitInfo(limitInfo);
    }

    const total = franchiseFee + brandUsageFee + designFee + storeServiceFee + equipmentCost + decorationCost;

    setInitialCosts({
      franchiseFee,
      brandUsageFee,
      designFee,
      storeServiceFee,
      equipmentCost,
      decorationCost,
      total
    });
  }, [filteredData.initialCostsData, basicInfo.area, setInitialCosts]);

  // 切换折叠状态
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // 计算各项费用占比
  const calculateRatio = (value: number): string => {
    if (initialCosts.total <= 0) return '0.0%';
    return `${((value / initialCosts.total) * 100).toFixed(1)}%`;
  };

  const displayValue = (_value: number): string => {
    return '****';
  };

  const displayRatio = (_value: number): string => {
    return '****';
  };

  return (
    <div className="mb-2">
      <h2 className="section-title title-initial-costs mb-0 w-full cursor-pointer" onClick={toggleCollapse}>
        建店成本<span className="text-xs text-gray-500 ml-1">（初始投入）</span>
      </h2>
      
      {!isCollapsed && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          <div className="form-group mb-1">
            <label htmlFor="franchiseFee" className="form-label mb-0 text-xs">
              加盟费（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.franchiseFee)))}
              </span>
            </label>
            <input 
              type="text" 
              id="franchiseFee" 
              className="form-input py-1 px-2 text-sm"
              value={displayValue(initialCosts.franchiseFee)}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="brandUsageFee" className="form-label mb-0 text-xs">
              品牌使用费（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.brandUsageFee)))}
              </span>
            </label>
            <input 
              type="text" 
              id="brandUsageFee" 
              className="form-input py-1 px-2 text-sm"
              value={displayValue(initialCosts.brandUsageFee)}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="designFee" className="form-label mb-0 text-xs">
              门店装修设计费（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.designFee)))}
              </span>
            </label>
            <input 
              type="text" 
              id="designFee" 
              className="form-input py-1 px-2 text-sm"
              value={displayValue(initialCosts.designFee)}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="storeServiceFee" className="form-label mb-0 text-xs">
              建店服务费（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.storeServiceFee)))}
              </span>
            </label>
            <input 
              type="text" 
              id="storeServiceFee" 
              className="form-input py-1 px-2 text-sm"
              value={displayValue(initialCosts.storeServiceFee)}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="equipmentCost" className="form-label mb-0 text-xs">
              设备成本（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.equipmentCost)))}
              </span>
            </label>
            <input 
              type="text" 
              id="equipmentCost" 
              className="form-input py-1 px-2 text-sm"
              value={displayValue(initialCosts.equipmentCost)}
              disabled
            />
          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="decorationCost" className="form-label mb-0 text-xs">
              门店装修成本（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(parseFloat(calculateRatio(initialCosts.decorationCost)))}
              </span>
            </label>
            <input
              type="text"
              id="decorationCost"
              className={`form-input py-1 px-2 text-sm ${decorationCostLimitInfo.isLimited ? 'border-orange-400' : ''}`}
              value={displayValue(initialCosts.decorationCost)}
              disabled
            />

          </div>
          
          <div className="form-group mb-1">
            <label htmlFor="initialCostsTotal" className="form-label mb-0 text-xs">
              合计（元）：
              <span className="ml-1 text-xs text-gray-500">
                占比：{displayRatio(100)}
              </span>
            </label>
            <input 
              type="text" 
              id="initialCostsTotal" 
              className="form-input py-1 px-2 text-sm font-semibold"
              value={displayValue(initialCosts.total)}
              disabled
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialCosts; 